<template>
  <div style="padding: 4px 0 24px;">
    <div class="split-toolbar-controls py-0 pl-2">
      <div class="toolbar-select-wrap">
        <v-select
          dense
          v-model="term"
          :items="termItems"
          label="Term"
          hide-details
          style="max-width: 130px;"
        />
      </div>
      <div class="toolbar-select-wrap">
        <v-select
          clearable
          dense
          v-model="setSplitName"
          :items="setSplitNames"
          label="Split Name"
          hide-details
          style="max-width: 180px;"
        />
      </div>
      <SchoolSplitHelpers
        :term="term"
        :set-split-name="setSplitName || ''"
        :set-split-names="setSplitNames"
        :student-count="(data && data.studentsWithFlagsCount) || 0"
        :all-students="(data && data.allStudents) || []"
        :has-existing-split="
          !!(
            data &&
            data.classesNumbersOnData &&
            data.classesNumbersOnData.length
          )
        "
        @editStudent="editStudent"
        @refresh="$emit('refresh')"
        @generated="$emit('generated', $event)"
      />
      <div v-if="data" class="split-summary pl-2">
        <div
          v-for="item in classConflictSummaries"
          :key="'conflict-' + item.classNumber"
          class="summary-pill summary-pill--conflict mr-2"
        >
          <v-icon small color="error">mdi-alert-circle</v-icon>
          <span>Class {{ item.classNumber }} - {{ item.count }} Conflicts</span>
        </div>
        <div v-if="totalEnrolledStudents > 0" class="summary-pill mr-2">
          <v-icon small color="success">mdi-school</v-icon>
          <span>{{ classRows.length }} Classes</span>
        </div>
        <div v-if="totalEnrolledStudents > 0" class="summary-pill mr-2">
          <v-icon small color="success">mdi-account-multiple</v-icon>
          <span>{{ totalEnrolledStudents }} Students</span>
        </div>
      </div>
    </div>

    <div v-if="!classRows.length" class="by-class-empty">
      <div class="by-class-empty-title">Select a Split to view classes</div>

    </div>
    <v-layout v-else>
      <v-flex class="skill-grid-scroll mx-2">
        <div class="student-group-card">
          <div class="student-group-card-main">
            <div class="student-group-side"></div>
            <div class="student-flag-cards" :style="flagCardsGridStyle">
              <div
                v-for="category in categoryColumns"
                :key="'h-' + category.categoryName"
                class="flag-card skill-cube skill-cube-header"
              >
                {{ category.categoryName }}
              </div>
            </div>
          </div>
        </div>

        <div
          v-for="row in classRows"
          :key="row.classNumber"
          :class="[
            'student-group-card',
            {
              'student-group-card--expanded':
                expandedClassNumber === row.classNumber,
            },
          ]"
        >
          <div class="student-group-card-main">
            <div class="student-group-side">
              <div class="student-group-side-main">
                <div class="class-number-label">{{ row.classNumber }}</div>
                <div class="class-meta-row pt-4">
                  <span class="class-meta-item" title="Students">
                    <v-icon size="10">mdi-account</v-icon>
                    {{ row.studentCount }}
                  </span>
                </div>
                <v-icon
                  :title="
                    expandedClassNumber === row.classNumber
                      ? 'Collapse'
                      : 'Expand'
                  "
                  @click.stop="toggleClassExpand(row.classNumber)"
                  class="class-row-expand-chevron"
                  size="28"
                >
                  {{
                    expandedClassNumber === row.classNumber
                      ? "mdi-chevron-up"
                      : "mdi-chevron-down"
                  }}
                </v-icon>
              </div>
            </div>

            <div class="student-flag-cards" :style="flagCardsGridStyle">
              <div
                v-for="cell in row.cells"
                :key="row.classNumber + '-' + cell.categoryName"
                :title="cell.categoryName"
                :class="[
                  'flag-card',
                  'skill-cube',
                  cell.distribution.length
                    ? 'skill-cube-filled'
                    : 'skill-cube-empty',
                ]"
              >
                <div v-if="cell.distribution.length" class="breakdown-dist">
                  <span
                    v-for="d in cell.distribution"
                    :key="
                      row.classNumber + '-' + cell.categoryName + '-' + d.flag
                    "
                    class="dist-pill dist-pill-clickable"
                    :class="{
                      'dist-pill-active': isFilterActive(
                        row.classNumber,
                        cell.categoryName,
                        d.flag
                      ),
                    }"
                    @click.stop="
                      toggleFilter(row.classNumber, cell.categoryName, d.flag)
                    "
                    :title="d.flag + ' (' + d.count + ' students)'"
                  >
                    <span class="dist-pill-flag">{{ d.flag }}</span>
                    <b class="dist-pill-count">{{ d.count }}</b>
                  </span>
                </div>
                <span v-else class="skill-cube-empty-label">No data</span>
              </div>
            </div>
          </div>

          <div
            v-if="expandedClassNumber === row.classNumber"
            class="students-cell-scroll class-students-expand"
          >
            <div v-if="row.conflictPairs.length" class="class-conflict-pairs">
              <div class="class-conflict-pairs-header">
                <v-icon size="12" color="#c62828">mdi-alert-circle</v-icon>
                <span>Conflicts</span>
                <span class="class-conflict-pairs-count">{{
                  row.conflictPairs.length
                }}</span>
              </div>
              <div class="class-conflict-pairs-list">
                <span
                  v-for="pair in row.conflictPairs"
                  :key="pair"
                  class="class-conflict-pill"
                >
                  {{ pair }}
                </span>
              </div>
            </div>
            <div
              v-for="student in visibleStudents(row)"
              :key="student.studentId"
              class="student-item"
              :class="{
                'student-item--expanded':
                  expandedStudentId === student.studentId,
              }"
            >
              <div class="student-pill" :title="student.displayName">
                <div class="student-pill-body">
                  <div
                    class="student-pill-avatar"
                    :class="{
                      'student-pill-avatar--clickable':
                        studentImgStarted[student.studentId] &&
                        !studentImgFailed[student.studentId],
                    }"
                    @click.stop="toggleStudentImgPreview(student.studentId)"
                  >
                    <img
                      v-if="
                        studentImgStarted[student.studentId] &&
                          !studentImgFailed[student.studentId]
                      "
                      :src="studentImgUrl(student.studentId)"
                      class="student-pill-img"
                      alt=""
                      @error="onStudentImgError(student.studentId)"
                    />
                    <div v-else class="student-pill-img-ph">
                      <v-icon size="16">mdi-account</v-icon>
                    </div>
                  </div>
                  <div class="student-pill-identity">
                    <span
                      class="student-pill-name"
                      :title="student.displayName"
                      >{{ student.displayName }}</span
                    >
                    <span
                      class="student-pill-eng-name"
                      :title="student.engName || ''"
                      >{{ student.engName || "—" }}</span
                    >
                  </div>
                  <div class="student-pill-meta">
                    <span
                      class="chip-meta-item chip-meta-item--id"
                      title="Student ID"
                    >
                      <v-icon size="12">mdi-identifier</v-icon
                      >{{ student.studentId || "—" }}
                    </span>
                    <span
                      class="chip-meta-item chip-meta-item--branch"
                      title="Branch"
                    >
                      <v-icon size="12">mdi-source-branch</v-icon
                      >{{ student.branch || "—" }}
                    </span>
                    <span
                      class="chip-meta-item chip-meta-item--building"
                      title="Building"
                    >
                      <v-icon size="12">mdi-domain</v-icon
                      >{{ student.building || "—" }}
                    </span>
                    <span
                      class="chip-meta-item chip-meta-item--class"
                      title="Class"
                    >
                      <v-icon size="12">mdi-school</v-icon
                      >{{ student.className || "—" }}
                    </span>
                    <span
                      class="chip-meta-item chip-meta-item--section"
                      title="Section"
                    >
                      <v-icon size="12">mdi-shape</v-icon
                      >{{ student.section || "—" }}
                    </span>
                  </div>
                  <div
                    class="student-pill-grades"
                    :style="{
                      gridTemplateColumns:
                        'repeat(' +
                        categoryColumns.length +
                        ', minmax(72px, 1fr))',
                    }"
                  >
                    <span
                      v-for="cat in categoryColumns"
                      :key="student.studentId + '-g-' + cat.categoryName"
                      class="chip-grade-item"
                      :class="{
                        'chip-grade-item--na': !student.flagsByCategory[
                          cat.categoryName
                        ],
                      }"
                      :title="cat.categoryName"
                    >
                      <b>{{
                        student.flagsByCategory[cat.categoryName] || "NA"
                      }}</b>
                      <span class="chip-grade-cat">{{ cat.categoryName }}</span>
                    </span>
                  </div>
                </div>
                <v-btn
                  flat
                  icon
                  :title="
                    hasStudentExcludes(student)
                      ? expandedStudentId === student.studentId
                        ? 'Hide Excludes'
                        : 'Show Excludes'
                      : null
                  "
                  @click.stop="toggleStudent(student.studentId)"
                  class="student-pill-chevron-edit"
                  :class="{
                    'student-pill-chevron-edit--hidden': !hasStudentExcludes(
                      student
                    ),
                  }"
                >
                  <v-icon size="18">
                    {{
                      expandedStudentId === student.studentId
                        ? "mdi-chevron-up"
                        : "mdi-account-off"
                    }} </v-icon
                  >{{ student.excludes.length + student.excludeBy.length }}
                </v-btn>
                <v-icon
                  :title="'Edit Student'"
                  size="18"
                  class="student-pill-chevron-edit"
                  @click.stop="editStudent(student.studentId)"
                  >mdi-pencil
                </v-icon>
              </div>
              <div
                v-if="expandedStudentId === student.studentId"
                class="student-details"
              >
                <div v-if="student.excludes.length" class="student-excludes">
                  <div class="student-excludes-title">Excludes</div>
                  <div class="student-excludes-list">
                    <div
                      v-for="exclude in student.excludes"
                      :key="exclude.excludeId"
                      class="student-exclude-pill"
                      :title="exclude.excludeEngName"
                    >
                      <span class="student-exclude-name">
                        {{
                          exclude.excludeHebName ||
                            exclude.excludeEngName ||
                            exclude.excludeId
                        }}
                      </span>
                      <span
                        v-if="exclude.excludeClass != null"
                        title="Enrolled to class"
                        class="student-exclude-class"
                      >
                        {{ exclude.excludeClass }}
                      </span>
                      <span
                        v-else
                        class="student-exclude-class student-exclude-class--none"
                      >
                        Not Enrolled
                      </span>
                    </div>
                  </div>
                </div>
                <div v-if="student.excludeBy.length" class="student-excludes">
                  <div class="student-excludes-title">Exclude By</div>
                  <div class="student-excludes-list">
                    <div
                      v-for="excludeBy in student.excludeBy"
                      :key="excludeBy.studentId"
                      class="student-exclude-pill"
                      :title="excludeBy.engName"
                    >
                      <span class="student-exclude-name">
                        {{
                          excludeBy.hebName ||
                            excludeBy.engName ||
                            excludeBy.studentId
                        }}
                      </span>
                      <span
                        title="Enrolled to class"
                        v-if="excludeBy.enrolledToClass != null"
                        class="student-exclude-class"
                      >
                        {{ excludeBy.enrolledToClass }}
                      </span>
                      <span
                        v-else
                        class="student-exclude-class student-exclude-class--none"
                      >
                        Not Enrolled
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </v-flex>
    </v-layout>

    <div
      v-if="previewStudentId"
      class="student-img-preview-backdrop"
      @click="closeStudentImgPreview"
    >
      <img
        :src="studentImgUrl(previewStudentId)"
        class="student-img-preview"
        alt=""
        @click.stop="closeStudentImgPreview"
      />
    </div>
  </div>
</template>

<script>
import SchoolSplitHelpers from "./SchoolSplitHelpers.vue";

export default {
  name: "SchoolSplitClassesView",
  components: {
    SchoolSplitHelpers,
  },
  watch: {
    initialTerm(val) {
      if (val !== this.term) this.term = val;
    },
    term() {
      this.$emit("update:initialTerm", this.term);
    },
    initialSetSplitName(val) {
      if (val !== this.setSplitName) this.setSplitName = val;
    },
    setSplitName() {
      this.$emit("update:initialSetSplitName", this.setSplitName);
    },
    allData: {
      handler(data) {
        this.data = data;
        this.activeFilter = null;
        this.expandedClassNumber = null;
        this.expandedStudentId = null;
        this.clearStudentImgQueue();
      },
      immediate: true,
    },
    classRows: {
      handler() {
        this.measureColumns();
      },
      immediate: true,
    },
  },
  props: {
    termItems: {
      type: Array,
      default: () => [],
    },
    initialTerm: {
      type: String,
      default: "",
    },
    setSplitNames: {
      type: Array,
      default: () => [],
    },
    initialSetSplitName: {
      default: null,
    },
    allData: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      term: this.initialTerm || "",
      setSplitName: this.initialSetSplitName || null,
      data: null,
      columnWidths: {},
      activeFilter: null,
      expandedClassNumber: null,
      expandedStudentId: null,
      studentImgStarted: {},
      studentImgFailed: {},
      studentImgQueueTimer: null,
      previewStudentId: null,
    };
  },
  beforeDestroy() {
    this.clearStudentImgQueue();
  },
  computed: {
    totalEnrolledStudents() {
      const enrolled = (this.data && this.data.enrolledClasses) || [];
      return enrolled.reduce(
        (sum, cls) => sum + ((cls.students && cls.students.length) || 0),
        0
      );
    },
    classConflictSummaries() {
      return this.classRows
        .filter((row) => row.conflictPairs && row.conflictPairs.length)
        .map((row) => ({
          classNumber: row.classNumber,
          count: row.conflictPairs.length,
        }));
    },
    categoryColumns() {
      const categories = (this.data && this.data.categories) || [];
      return categories
        .map((c) => ({
          categoryName: String((c && c.categoryName) || "").trim(),
        }))
        .filter((c) => c.categoryName)
        .sort((a, b) => a.categoryName.localeCompare(b.categoryName));
    },
    flagCardsGridStyle() {
      const cols = this.categoryColumns;
      if (!cols.length) return {};
      const template = cols
        .map((c) => {
          const width = this.columnWidths[c.categoryName];
          return width ? `${width}fr` : "minmax(64px, 1fr)";
        })
        .join(" ");
      return { gridTemplateColumns: template };
    },
    classRows() {
      const enrolled = (this.data && this.data.enrolledClasses) || [];
      const columns = this.categoryColumns;
      if (!enrolled.length) return [];

      return enrolled
        .slice()
        .sort((a, b) => Number(a.classNumber) - Number(b.classNumber))
        .map((cls) => {
          const students = cls.students || [];
          const cells = columns.map((category) => {
            const categoryName = category.categoryName;
            const counts = {};
            students.forEach((student) => {
              (student.flags || []).forEach((entry) => {
                if (entry.isDeleted) return;
                const cat = String(entry.category || "").trim();
                if (cat !== categoryName) return;
                const flag = String(entry.flag || "").trim();
                if (!flag) return;
                counts[flag] = (counts[flag] || 0) + 1;
              });
            });
            const distribution = Object.keys(counts)
              .sort()
              .map((flag) => ({ flag: flag, count: counts[flag] }));
            return {
              categoryName: categoryName,
              distribution: distribution,
            };
          });
          const studentList = students.map((student) => {
            const hebName = (
              String(student.hebFirstName || "").trim() +
              " " +
              String(student.hebLastName || "").trim()
            ).trim();
            const engName = (
              String(student.firstName || "").trim() +
              " " +
              String(student.lastName || "").trim()
            ).trim();
            const studentId = String(student.studentId || "").trim();
            const excludes = (student.excludes || [])
              .filter((entry) => !entry.isDeleted)
              .map((entry) => {
                const excludeHebName = (
                  String(entry.hebFirstName || "").trim() +
                  " " +
                  String(entry.hebLastName || "").trim()
                ).trim();
                const excludeEngName = (
                  String(entry.firstName || "").trim() +
                  " " +
                  String(entry.lastName || "").trim()
                ).trim();
                const excludeId = String(entry.studentId || "").trim();
                const excludeClass = String(
                  (entry.enrolledToClass &&
                    entry.enrolledToClass.classNumber) ||
                    ""
                ).trim();
                return {
                  excludeId: excludeId,
                  excludeHebName: excludeHebName,
                  excludeEngName: excludeEngName,
                  excludeClass: excludeClass,
                };
              });
            const excludeBy = (student.excludeBy || [])
              .filter((entry) => !entry.isDeleted)
              .map((entry) => {
                const studentId = String(entry.studentId || "").trim();
                const hebName = (
                  String(entry.hebFirstName || "").trim() +
                  " " +
                  String(entry.hebLastName || "").trim()
                ).trim();
                const engName = (
                  String(entry.firstName || "").trim() +
                  " " +
                  String(entry.lastName || "").trim()
                ).trim();
                const enrolledToClass = String(
                  (entry.enrolledToClass &&
                    entry.enrolledToClass.classNumber) ||
                    ""
                ).trim();
                return {
                  studentId,
                  hebName,
                  engName,
                  enrolledToClass,
                };
              });
            const flagsByCategory = {};
            (student.flags || []).forEach((entry) => {
              if (entry.isDeleted) return;
              const category = String(entry.category || "").trim();
              const flag = String(entry.flag || "").trim();
              if (!category || !flag) return;
              flagsByCategory[category] = flag;
            });
            return {
              studentId: studentId,
              displayName: hebName || engName || studentId,
              engName: engName,
              className: String(student.class || "").trim(),
              branch: String(student.branch || "").trim(),
              building: String(student.building || "").trim(),
              section: String(student.section || "").trim(),
              excludes: excludes,
              excludeBy,
              flagsByCategory,
            };
          });
          const classNumber = String(cls.classNumber || "").trim();
          const seenConflicts = {};
          const conflictPairs = [];
          studentList.forEach((student) => {
            (student.excludes || []).forEach((exclude) => {
              const excludeClass = String(exclude.excludeClass || "").trim();
              if (!excludeClass || excludeClass !== classNumber) return;
              const otherId = String(exclude.excludeId || "").trim();
              if (!otherId) return;
              const key = [student.studentId, otherId].sort().join("|");
              if (seenConflicts[key]) return;
              seenConflicts[key] = true;
              const otherName =
                exclude.excludeHebName || exclude.excludeEngName || otherId;
              conflictPairs.push(student.displayName + " ↔ " + otherName);
            });
          });
          return {
            classNumber: cls.classNumber,
            studentCount: students.length,
            students: studentList,
            cells: cells,
            conflictPairs,
          };
        });
    },
  },
  methods: {
    isFilterActive(classNumber, category, flag) {
      const f = this.activeFilter;
      return !!(
        f &&
        f.classNumber === classNumber &&
        f.category === category &&
        f.flag === flag
      );
    },

    toggleFilter(classNumber, category, flag) {
      if (this.expandedClassNumber === null) return;
      this.expandedStudentId = null;
      if (this.isFilterActive(classNumber, category, flag)) {
        this.activeFilter = null;
      } else {
        this.activeFilter = {
          classNumber: classNumber,
          category: category,
          flag: flag,
        };
        this.expandedClassNumber = classNumber;
      }
      this.clearStudentImgQueue();
      if (this.expandedClassNumber == null) return;
      const row = this.classRows.find(
        (r) => r.classNumber === this.expandedClassNumber
      );
      this.startStudentImgQueue(this.visibleStudents(row || { students: [] }));
    },

    visibleStudents(row) {
      const f = this.activeFilter;
      if (!row || !row.students) return [];
      if (!f || f.classNumber !== row.classNumber) return row.students;
      return row.students.filter(
        (student) =>
          student.flagsByCategory &&
          student.flagsByCategory[f.category] === f.flag
      );
    },
    // Cube height is capped, so pills wrap into columns. Column weights are
    // derived from the densest cell so every row shares the same grid tracks.
    measureColumns() {
      const pillWidth = 71;
      const pillHeight = 29;
      const maxHeight = 87;
      const cellPad = 14;
      const pillsPerCol = Math.max(1, Math.floor(maxHeight / pillHeight));
      const widths = {};

      this.categoryColumns.forEach((category) => {
        const name = category.categoryName;
        const headerWidth = Math.ceil(name.length * 7.2) + cellPad;
        widths[name] = Math.max(64, headerWidth);
      });

      this.classRows.forEach((row) => {
        (row.cells || []).forEach((cell) => {
          const count = (cell.distribution && cell.distribution.length) || 0;
          const colsNeeded = Math.max(1, Math.ceil(count / pillsPerCol));
          const width = colsNeeded * pillWidth + cellPad;
          widths[cell.categoryName] = Math.max(
            widths[cell.categoryName] || 64,
            width
          );
        });
      });

      this.columnWidths = widths;
    },

    toggleClassExpand(classNumber) {
      this.activeFilter = null;
      const next =
        this.expandedClassNumber === classNumber ? null : classNumber;
      this.expandedClassNumber = next;
      this.clearStudentImgQueue();
      if (next == null) return;
      const row = this.classRows.find((r) => r.classNumber === next);
      this.startStudentImgQueue(this.visibleStudents(row || { students: [] }));
    },
    toggleStudent(studentId) {
      this.expandedStudentId =
        this.expandedStudentId === studentId ? null : studentId;
    },
    hasStudentExcludes(student) {
      return (
        !!(student.excludes && student.excludes.length) ||
        !!(student.excludeBy && student.excludeBy.length)
      );
    },
    studentImgUrl(studentId) {
      return (
        "https://attendance.wiseaccesstech.com/api/Attendance/GetStudentImg?school=UTA&studentId=" +
        encodeURIComponent(studentId)
      );
    },
    clearStudentImgQueue() {
      if (this.studentImgQueueTimer) {
        clearTimeout(this.studentImgQueueTimer);
        this.studentImgQueueTimer = null;
      }
      this.studentImgStarted = {};
      this.studentImgFailed = {};
      this.previewStudentId = null;
    },
    startStudentImgQueue(students) {
      const ids = (students || [])
        .map((s) => String(s.studentId || "").trim())
        .filter(Boolean);
      let i = 0;
      const loadNext = () => {
        if (i >= ids.length) {
          this.studentImgQueueTimer = null;
          return;
        }
        this.$set(this.studentImgStarted, ids[i], true);
        i += 1;
        this.studentImgQueueTimer = setTimeout(loadNext, 80);
      };
      loadNext();
    },
    onStudentImgError(studentId) {
      this.$set(this.studentImgFailed, studentId, true);
      if (this.previewStudentId === studentId) this.previewStudentId = null;
    },
    toggleStudentImgPreview(studentId) {
      if (
        !this.studentImgStarted[studentId] ||
        this.studentImgFailed[studentId]
      ) {
        return;
      }
      this.previewStudentId =
        this.previewStudentId === studentId ? null : studentId;
    },
    closeStudentImgPreview() {
      this.previewStudentId = null;
    },
    editStudent(studentId) {
      this.$emit("editStudent", studentId);
    },
  },
};
</script>

<style scoped>
.split-toolbar-controls {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.toolbar-select-wrap {
  background: #f3f2f2;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1.5px solid #d0d9d1;
  border-radius: 15px;
  padding: 0 12px 0 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  transition: border-color 0.2s;
}

.toolbar-select-wrap:focus-within {
  border-color: #4caf50;
  box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.12);
}

.split-summary {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex-wrap: wrap;
  margin-left: auto;
}

.summary-pill {
  display: flex;
  gap: 6px;
  background: #f1f8f2;
  border: 1px solid #dceadd;
  color: #2e5d33;
  border-radius: 20px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.summary-pill--conflict {
  background: #fdecea;
  border-color: #f5c6c2;
  color: #c62828;
}

.by-class-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 16px;
  color: #9e9e9e;
}

.by-class-empty-title {
  margin-top: 12px;
  font-size: 16px;
  font-weight: 700;
  color: #757575;
}

.skill-grid-scroll {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  padding-bottom: 4px;
}

.student-group-card {
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid #e0e0e0;
  border-left: 4px solid #5cb860;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  width: 100%;
  min-width: 0;
  transition: background 0.15s ease, box-shadow 0.15s ease,
    border-color 0.15s ease;
}

.student-group-card-main {
  position: relative;
  display: flex;
  gap: 0;
  min-width: 0;
  width: 100%;
}

.class-row-expand-chevron {
  color: #43a047 !important;
  cursor: pointer;
  margin-top: 2px;
}
.class-row-expand-chevron:hover {
  color: #2e7d32 !important;
  transform: scale(1.2);
}

.student-group-card--expanded {
  border-color: #81c784;
}

.class-students-expand {
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
  padding: 0;
  border-top: 1px solid #e0e0e0;
  background: #fff;
  box-sizing: border-box;
}

.student-group-side {
  display: flex;
  width: 60px;
  flex-shrink: 0;
}

.student-group-side-main {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  flex: 1;
  min-width: 0;
  text-align: center;
  padding: 8px 4px;
}
.class-number-label {
  position: absolute;
  top: 6px;
  left: 6px;
  font-weight: 700;
  font-size: 15px;
  line-height: 1;
  color: #333;
}

.class-meta-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 3px 8px;
}

.class-meta-item {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 20px;
  font-weight: 600;
  color: #616161;
}

.class-meta-item .v-icon {
  font-size: 20px !important;
  color: #7cb342 !important;
}

.student-flag-cards {
  display: grid;
  align-items: stretch;
  gap: 5px;
  flex: 1;
  min-width: 0;
  padding: 6px 8px;
}

.flag-card {
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 5px 6px;
  background: linear-gradient(180deg, #fbfdfb 0%, #f5f7f5 100%);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  min-width: 0;
}

.skill-cube {
  width: 100%;
  min-width: 0;
  overflow: hidden;
  display: flex;
  box-sizing: border-box;
}

.skill-cube-header {
  background: #e8f5e9;
  align-items: center;
  justify-content: center;
  border-color: #c8e6c9;
  color: #2e7d32;
  font-size: 12px;
  font-weight: 700;
  text-align: center;
  word-break: break-word;
  height: auto;
  min-height: 34px;
}

.skill-cube-filled {
  background: #fff;
  border-color: #c8e6c9;
}

.skill-cube-filled:hover {
  border-color: #a5d6a7;
  box-shadow: 0 2px 6px rgba(92, 184, 96, 0.12);
}

.skill-cube-empty {
  background: #f3f6f3;
  border: 1px dashed #d0d7d0;
  color: #b0b8b0;
  align-items: center;
  justify-content: center;
}

.skill-cube-empty-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: #b0b8b0;
  text-transform: uppercase;
}

.breakdown-dist {
  display: flex;
  flex-flow: column wrap;
  gap: 3px 6px;
  height: 87px;
}

.dist-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #fff;
  border: 1px solid #e0e6e0;
  border-radius: 12px;
  width: 65px;
  padding: 2px 7px;
  font-size: 14px;
  color: #546e7a;
}

.dist-pill-flag {
  font-weight: 700;
  font-size: 14px;
  color: #37474f;
}

.dist-pill-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 10px;
  background: #eceff1;
  color: #263238 !important;
  font-size: 12px;
  font-weight: 700;
}

.dist-pill-clickable {
  cursor: pointer;
  transition: all 0.15s ease;
}

.dist-pill-clickable:hover {
  border-color: #4caf50;
  background: #f1f8f2;
}

.dist-pill-active {
  background: #4caf50;
  border-color: #4caf50;
  color: #fff;
}

.dist-pill-active .dist-pill-flag {
  color: #fff;
}

.dist-pill-active .dist-pill-count {
  background: rgba(255, 255, 255, 0.25);
  color: #fff !important;
}

.dist-pill-active b {
  color: #fff;
}

.students-cell-scroll {
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 0;
  overflow-y: auto;
  width: 100%;
  box-sizing: border-box;
}

.student-item {
  border: none;
  border-bottom: 1px solid #eceff1;
  border-radius: 0;
  background: #fff;
  overflow: hidden;
  transition: background 0.12s ease;
}

.student-item:last-child {
  border-bottom: none;
}

.student-item:hover {
  background: #f8faf8;
}

.student-item--expanded {
  background: #f3f8f4;
}

.student-pill {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  padding: 0px 14px;
  direction: ltr;
}

.student-pill-body {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 14px;
  flex: 1;
  min-width: 0;
  overflow-x: auto;
  direction: ltr;
}

.student-pill-identity {
  display: flex;
  flex-wrap: nowrap;
  align-items: baseline;
  gap: 8px;
  flex-shrink: 0;
}

.student-pill-avatar {
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
}

.student-pill-avatar--clickable {
  cursor: zoom-in;
}

.student-pill-img,
.student-pill-img-ph {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  display: block;
  background: #e8f0ea;
}

.student-pill-img-ph {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9e9e9e;
}

.student-pill-img-ph .v-icon {
  color: #b0bec5 !important;
}

.student-img-preview-backdrop {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(33, 33, 33, 0.45);
  cursor: zoom-out;
}

.student-img-preview {
  width: min(72vw, 320px);
  height: min(72vw, 320px);
  border-radius: 18px;
  object-fit: cover;
  background: #fff;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.35);
  cursor: zoom-out;
}

.student-pill-name {
  width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.25;
  color: #1b5e20;
  direction: rtl;
  text-align: left;
}

.student-pill-eng-name {
  width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  color: #90a4ae;
  direction: ltr;
  text-align: left;
}

.student-pill-meta {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  padding-inline: 12px;
  border-inline-start: 1px solid #e8ece8;
  border-inline-end: 1px solid #e8ece8;
}

.student-pill-grades {
  display: grid;
  gap: 6px;
  flex: 1;
  min-width: 0;
}

.student-pill-chevron-edit {
  flex: 0 0 24px;
  width: 24px;
  color: #b0bec5 !important;
  cursor: pointer;
  transition: color 0.12s ease, transform 0.12s ease;
}

.student-pill-chevron-edit--hidden {
  visibility: hidden;
  pointer-events: none;
}

.student-pill-chevron-edit:hover {
  color: #43a047 !important;
  transform: scale(1.35);
}

.student-details {
  padding: 2px 14px 10px;
  border-top: none;
  background: transparent;
}

.chip-meta-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 11px;
  font-weight: 500;
  color: #607d8b;
  white-space: nowrap;
  background: transparent;
  border-radius: 0;
  padding: 0;
}

.chip-meta-item--id {
  width: 78px;
}

.chip-meta-item--branch {
  width: 48px;
}

.chip-meta-item--building {
  width: 40px;
}

.chip-meta-item--class {
  width: 44px;
}

.chip-meta-item--section {
  width: 48px;
}

.chip-meta-item .v-icon {
  color: #b0bec5 !important;
  flex-shrink: 0;
}

.chip-grade-item {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  background: #f1f6f2;
  border: none;
  border-radius: 6px;
  padding: 3px 6px;
  font-size: 11px;
  color: #546e7a;
  white-space: nowrap;
}

.chip-grade-cat {
  overflow: hidden;
  text-overflow: ellipsis;
  color: #78909c;
  font-weight: 500;
  min-width: 0;
}

.chip-grade-item b {
  flex-shrink: 0;
  color: #2e7d32;
  font-weight: 700;
}

.chip-grade-item--na {
  background: #f5f5f5;
}

.chip-grade-item--na b,
.chip-grade-item--na .chip-grade-cat {
  color: #bdbdbd;
  font-weight: 500;
}

.student-excludes {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 6px;
  padding-top: 6px;
}

.student-excludes + .student-excludes {
  border-top: 1px dashed #e0e0e0;
}

.student-excludes-title {
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 700;
  color: #78909c;
  line-height: 22px;
  min-width: 72px;
}

.student-excludes-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  min-width: 0;
  flex: 1;
}

.student-exclude-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  width: fit-content;
  max-width: 100%;
  background: #fff3e0;
  border: 1px solid #ffe0b2;
  border-radius: 12px;
  padding: 2px 6px 2px 4px;
  font-size: 11px;
  font-weight: 600;
  color: #ef6c00;
  direction: rtl;
}

.student-exclude-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: right;
}

.student-exclude-class {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
  background: #fff;
  border: 1px solid #ffcc80;
  border-radius: 8px;
  padding: 0 6px;
  font-size: 11px;
  font-weight: 700;
  color: #e65100;
  white-space: nowrap;
}

.student-exclude-class--none {
  background: #fafafa;
  border-color: #e0e0e0;
  color: #9e9e9e;
  font-weight: 600;
}

.class-conflict-pairs {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  align-self: flex-start;
  width: fit-content;
  max-width: 100%;
  gap: 4px 6px;
  margin: 6px 10px 2px;
  padding: 4px 8px;
  background: #fff5f5;
  border: 1px solid #ffcdd2;
  border-radius: 8px;
  box-sizing: border-box;
}

.class-conflict-pairs-header {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #c62828;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
}

.class-conflict-pairs-count {
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: #c62828;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
}

.class-conflict-pairs-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  min-width: 0;
}

.class-conflict-pill {
  display: inline-block;
  max-width: 220px;
  background: #fff;
  border: 1px solid #ffcdd2;
  border-radius: 6px;
  padding: 1px 6px;
  color: #b71c1c;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
